import { AxiosInstance, AxiosResponse } from "axios";
import { httpStatusIsOk } from "../../common/http";
import { ErrorResponse, greenRouterAPI, rejectRequest } from "../api";
import { ResourceUri } from "../requests/common";

const makeUrl = (uri: ResourceUri): string => {
	let url = `${uri.route}`;

	// Build the resource URI path.
	if (uri.path) {
		for (const [k, v] of Object.entries(uri.path)) {
			// Undefined values will result in an in valid URI, but this is acceptable in this context, as the
			// networking layer will send a request that will probably result in a 404 HTTP error.
			url = url.replaceAll(`{${k}}`, `${v ?? "-"}`);
		}
	}

	// Build optional query string.
	if (uri.query) {
		const query: string[] = [];
		for (const [k, v] of Object.entries(uri.query)) {
			if (v !== undefined && v !== null) {
				query.push(`${k}=${encodeURIComponent(v)}`);
			}
		}

		if (query.length > 0) {
			url = `${url}?${query.join("&")}`;
		}
	}

	return uri.resourceId ? `${url}/${uri.resourceId}` : url;
};

/**
 * Sends a GET request to the target location (backend main entry point).
 * If location is undefined, uses greenRouterAPI as the default location.
 *
 * @param uri
 * @param location
 * @param preferredStatus
 */
export const sendGet = async <Res>(
	uri: ResourceUri,
	location?: AxiosInstance,
	preferredStatus?: number
): Promise<Res> => {
	return send(uri, "get", location, preferredStatus) as Promise<Res>;
};

/**
 * Sends a POST request to the target location (backend main entry point).
 * If location is undefined, uses greenRouterAPI as the default location.
 *
 * @param payload
 * @param uri
 * @param location
 * @param preferredStatus
 */
export const sendPost = async <Res, Req>(
	payload: Req,
	uri: ResourceUri,
	location?: AxiosInstance,
	preferredStatus?: number
): Promise<Res> => {
	return send(uri, "post", location, payload, preferredStatus) as Promise<Res>;
};

/**
 * Sends a PUT request to the target location (backend main entry point).
 * If location is undefined, uses greenRouterAPI as the default location.
 *
 * @param payload
 * @param uri
 * @param location
 * @param preferredStatus
 */
export const sendPut = async <Res, Req>(
	payload: Req,
	uri: ResourceUri,
	location?: AxiosInstance,
	preferredStatus?: number
): Promise<Res> => {
	return send(uri, "put", location, payload, preferredStatus) as Promise<Res>;
};

/**
 * Sends a DELETE request to the target location (backend main entry point).
 * If location is undefined, uses greenRouterAPI as the default location.
 *
 * @param uri
 * @param location
 * @param preferredStatus
 */
export const sendDelete = async <Res>(
	uri: ResourceUri,
	location?: AxiosInstance,
	preferredStatus?: number
): Promise<Res> => {
	return send(uri, "delete", location, undefined, preferredStatus) as Promise<Res>;
};

const send = async <Res, Req>(
	uri: ResourceUri,
	method: "get" | "post" | "put" | "delete",
	location?: AxiosInstance,
	payload?: Req,
	preferredStatus?: number
): Promise<Res | void> => {
	const url = makeUrl(uri);
	let response: AxiosResponse<Res>;
	const server = location ?? greenRouterAPI;

	try {
		switch (method) {
			case "get":
				response = await server.get<Res>(url);
				break;
			case "post":
				response = await server.post<Res>(url, payload);
				break;
			case "put":
				response = await server.put<Res>(url, payload);
				break;
			case "delete":
				response = await server.delete<Res>(url);
				break;
		}

		if (httpStatusIsOk(response.status)) {
			return response.data ? Promise.resolve(response.data) : Promise.resolve();
		}

		const errorData = response.data as ErrorResponse;
		return Promise.reject({ ...errorData, httpCode: response.status });
	} catch (e: any) {
		return rejectRequest(e, preferredStatus);
	}
};

export const sendPostPdf = async <Req>(payload: Req, uri: ResourceUri, location?: AxiosInstance): Promise<Blob> => {
	const server = location ?? greenRouterAPI;
	const url = makeUrl(uri);

	const response = await server.post(url, payload, { responseType: "blob" });
	return response.data;
};
